import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { SupabaseClient } from '@supabase/supabase-js'
import { useSupabaseClient } from 'src/api/supabase'
import type { Database, Json } from 'supabase/database.types'
import { queryKeys } from './queryKeys'

export const ANALYTICS_EVENT_NAMES = [
  'login',
  'request_open',
  'request_created',
  'share_open',
  'share_quote_open',
  'chat_sent',
  'checkout_open',
  'item_change',
  'catalog_open',
  'page_view',
  'org_added',
  'org_signup',
] as const

export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[number]
export type AnalyticsSurface = 'app' | 'share'

const VISITOR_STORAGE_KEY = 'klayder-analytics-visitor-id'
const ONCE_STORAGE_PREFIX = 'klayder-analytics-once:'

type DbClient = SupabaseClient<Database>

export interface AnalyticsOverview {
  active_orgs: number
  active_users: number
  logins: number
  share_opens: number
  request_created: number
  chat_sent: number
  request_opens: number
  checkout_open: number
  catalog_open: number
  item_change: number
  org_added: number
  org_signup: number
  event_count: number
  daily: Array<{
    day: string
    count: number
    logins: number
    share_opens: number
    request_opens: number
    request_created: number
    chat_sent: number
    org_added: number
    org_signup: number
  }>
}

export interface AnalyticsOrgRow {
  id: string
  name: string | null
  event_count: number
  active_users: number
  logins: number
  share_opens: number
  request_created: number
  chat_sent: number
  last_active_at: string | null
}

export interface AnalyticsUserRow {
  id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  org_id: string | null
  org_name: string | null
  event_count: number
  logins: number
  request_created: number
  chat_sent: number
  last_login_at: string | null
  last_active_at: string | null
}

export interface AnalyticsPageRow {
  path: string
  views: number
  unique_visitors: number
  last_viewed_at: string | null
}

export interface AnalyticsPages {
  page_views: number
  unique_visitors: number
  pages: AnalyticsPageRow[]
  daily: Array<{
    day: string
    views: number
    unique_visitors: number
  }>
}

export interface AnalyticsQuoteSummary {
  share_opens: number
  unique_visitors: number
  request_opens: number
  chat_sent: number
  checkout_open: number
  item_change: number
  catalog_open: number
  last_share_at: string | null
  last_activity_at: string | null
  daily: Array<{
    day: string
    share: number
    in_app: number
    engagement: number
  }>
  recent: Array<{
    created_at: string
    name: AnalyticsEventName
    user_id: string | null
    visitor_id: string | null
    user_email: string | null
    user_first_name: string | null
    user_last_name: string | null
  }>
}

const emptyOverview = (): AnalyticsOverview => ({
  active_orgs: 0,
  active_users: 0,
  logins: 0,
  share_opens: 0,
  request_created: 0,
  chat_sent: 0,
  request_opens: 0,
  checkout_open: 0,
  catalog_open: 0,
  item_change: 0,
  org_added: 0,
  org_signup: 0,
  event_count: 0,
  daily: [],
})

const emptyPages = (): AnalyticsPages => ({
  page_views: 0,
  unique_visitors: 0,
  pages: [],
  daily: [],
})

const emptyQuoteSummary = (): AnalyticsQuoteSummary => ({
  share_opens: 0,
  unique_visitors: 0,
  request_opens: 0,
  chat_sent: 0,
  checkout_open: 0,
  item_change: 0,
  catalog_open: 0,
  last_share_at: null,
  last_activity_at: null,
  daily: [],
  recent: [],
})

const parseJson = <T>(value: Json | null, fallback: T): T => {
  let parsed: unknown = value
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed)
    } catch {
      return fallback
    }
  }
  if (parsed == null || typeof parsed !== 'object') return fallback
  return parsed as T
}

const parseJsonRows = <T>(value: Json | null): T[] => {
  const parsed = parseJson<T[]>(value, [])
  return Array.isArray(parsed) ? parsed : []
}

export const getAnalyticsVisitorId = (): string | undefined => {
  try {
    const existing = window.localStorage.getItem(VISITOR_STORAGE_KEY)
    if (existing) return existing
    const id = crypto.randomUUID()
    window.localStorage.setItem(VISITOR_STORAGE_KEY, id)
    return id
  } catch {
    return undefined
  }
}

const markOnce = (key: string): boolean => {
  try {
    const storageKey = `${ONCE_STORAGE_PREFIX}${key}`
    if (window.sessionStorage.getItem(storageKey)) return false
    window.sessionStorage.setItem(storageKey, '1')
    return true
  } catch {
    return true
  }
}

export const landingPagePath = (pathname: string): string | null => {
  const path = pathname.replace(/\/+$/, '') || '/'
  if (path === '/' || path.startsWith('/c/')) return '/'
  if (path === '/about') return '/about'
  if (path === '/contact') return '/contact'
  if (path === '/all-products') return '/all-products'
  if (path.startsWith('/product/')) return '/product'
  if (path.startsWith('/for/')) return path
  if (path.startsWith('/login-signup') || path === '/login' || path === '/signup') {
    return '/login-signup'
  }
  if (path.startsWith('/invitation')) return '/invitation'
  if (path === '/privacy-policy' || path === '/privacyPolicy') return '/privacy-policy'
  if (path === '/terms-of-service' || path === '/termsOfService') {
    return '/terms-of-service'
  }
  if (path === '/cookieConsent') return '/cookieConsent'
  if (path.startsWith('/guided-assistant')) return '/guided-assistant'
  return null
}

export const landingPageLabel = (
  path: string,
  t: (en: string, sv: string) => string
) => {
  switch (path) {
    case '/':
      return t('Home', 'Hem')
    case '/about':
      return t('About', 'Om oss')
    case '/contact':
      return t('Contact', 'Kontakt')
    case '/all-products':
      return t('All products', 'Alla produkter')
    case '/product':
      return t('Product', 'Produkt')
    case '/login-signup':
      return t('Log in / Sign up', 'Logga in / Registrera')
    case '/invitation':
      return t('Invitation', 'Inbjudan')
    case '/privacy-policy':
      return t('Privacy policy', 'Integritetspolicy')
    case '/terms-of-service':
      return t('Terms of service', 'Användarvillkor')
    case '/cookieConsent':
      return t('Cookie consent', 'Cookie-samtycke')
    case '/guided-assistant':
      return t('Guided assistant', 'Guided assistant')
    case '/for/hr':
      return t('For HR', 'För HR')
    case '/for/marketing':
      return t('For marketing', 'För marketing')
    case '/for/operations':
      return t('For operations', 'För operations')
    case '/for/events':
      return t('For events', 'För events')
    case '/for/founders':
      return t('For founders', 'För founders')
    default:
      return path
  }
}

export const trackEvent = (
  supabase: DbClient,
  {
    name,
    quoteId,
    orgId,
    surface,
    onceKey,
    properties,
  }: {
    name: AnalyticsEventName
    quoteId?: number | null
    orgId?: string | null
    surface?: AnalyticsSurface
    onceKey?: string
    properties?: Record<string, string>
  }
) => {
  if (onceKey && !markOnce(onceKey)) return

  const resolvedSurface: AnalyticsSurface =
    surface ?? (name.startsWith('share_') ? 'share' : 'app')

  void supabase
    .rpc('track_analytics_event', {
      p_name: name,
      p_surface: resolvedSurface,
      p_quote_id: quoteId ?? undefined,
      p_visitor_id: getAnalyticsVisitorId(),
      p_properties: (properties ?? {}) as Json,
      p_org_id: orgId ?? undefined,
    })
    .then(({ error }) => {
      if (error) {
        console.error('Failed to track analytics event', name, error)
      }
    })
}

export const useTrackLandingPageView = () => {
  const supabase = useSupabaseClient()
  const location = useLocation()

  useEffect(() => {
    const path = landingPagePath(location.pathname)
    if (!path) return
    trackEvent(supabase, {
      name: 'page_view',
      properties: { path },
    })
  }, [location.pathname, supabase])
}

export const rangeFromDays = (days: number) => {
  const to = new Date()
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

export const useAnalyticsOverview = (days: number) => {
  const supabase = useSupabaseClient()
  const { from, to } = rangeFromDays(days)

  return useQuery({
    queryKey: [queryKeys.analyticsOverview, days],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('analytics_overview', {
        p_from: from,
        p_to: to,
      })
      if (error) throw error
      return parseJson(data, emptyOverview())
    },
    placeholderData: (previous) => previous,
  })
}

export const useAnalyticsOrgs = (days: number) => {
  const supabase = useSupabaseClient()
  const { from, to } = rangeFromDays(days)

  return useQuery({
    queryKey: [queryKeys.analyticsOrgs, days],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('analytics_orgs', {
        p_from: from,
        p_to: to,
      })
      if (error) throw error
      return parseJsonRows<AnalyticsOrgRow>(data)
    },
    placeholderData: (previous) => previous,
  })
}

export const useAnalyticsUsers = (days: number) => {
  const supabase = useSupabaseClient()
  const { from, to } = rangeFromDays(days)

  return useQuery({
    queryKey: [queryKeys.analyticsUsers, days],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('analytics_users', {
        p_from: from,
        p_to: to,
      })
      if (error) throw error
      return parseJsonRows<AnalyticsUserRow>(data)
    },
    placeholderData: (previous) => previous,
  })
}

export const useAnalyticsPages = (days: number) => {
  const supabase = useSupabaseClient()
  const { from, to } = rangeFromDays(days)

  return useQuery({
    queryKey: [queryKeys.analyticsPages, days],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('analytics_pages', {
        p_from: from,
        p_to: to,
      })
      if (error) throw error
      return parseJson(data, emptyPages())
    },
    placeholderData: (previous) => previous,
  })
}

export const useAnalyticsQuoteSummary = (
  quoteId: number | undefined,
  enabled: boolean
) => {
  const supabase = useSupabaseClient()

  return useQuery({
    queryKey: [queryKeys.analyticsQuote, quoteId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('analytics_quote_summary', {
        p_quote_id: quoteId!,
      })
      if (error) throw error
      return parseJson(data, emptyQuoteSummary())
    },
    enabled: enabled && quoteId != null,
  })
}

export const formatRelativeTime = (
  iso: string | null | undefined,
  locale: 'en' | 'sv' = 'en'
) => {
  if (!iso) return locale === 'sv' ? 'Aldrig' : 'Never'
  const diffMs = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(diffMs)) return locale === 'sv' ? 'Aldrig' : 'Never'
  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour
  const rtf = new Intl.RelativeTimeFormat(locale === 'sv' ? 'sv' : 'en', {
    numeric: 'auto',
  })
  if (Math.abs(diffMs) < hour) {
    return rtf.format(-Math.round(diffMs / minute) || 0, 'minute')
  }
  if (Math.abs(diffMs) < day) {
    return rtf.format(-Math.round(diffMs / hour), 'hour')
  }
  return rtf.format(-Math.round(diffMs / day), 'day')
}

export const analyticsEventLabel = (
  name: string,
  t: (en: string, sv: string) => string
) => {
  switch (name) {
    case 'login':
      return t('Logged in', 'Loggade in')
    case 'request_open':
      return t('Opened request', 'Öppnade förfrågan')
    case 'request_created':
      return t('Created a request', 'Skapade en förfrågan')
    case 'share_open':
      return t('Opened share link', 'Öppnade dellänk')
    case 'share_quote_open':
      return t('Opened quote on share page', 'Öppnade offert på delsida')
    case 'chat_sent':
      return t('Sent a message', 'Skickade ett meddelande')
    case 'checkout_open':
      return t('Opened checkout', 'Öppnade kassan')
    case 'item_change':
      return t('Changed products', 'Ändrade produkter')
    case 'catalog_open':
      return t('Opened product catalog', 'Öppnade produktkatalogen')
    case 'page_view':
      return t('Viewed a page', 'Besökte en sida')
    case 'org_added':
      return t('Organization added', 'Organisation tillagd')
    case 'org_signup':
      return t('Signed up', 'Registrerade sig')
    default:
      return name
  }
}

export const displayPersonName = ({
  firstName,
  lastName,
  email,
  fallback,
}: {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  fallback: string
}) => {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim()
  return name || email || fallback
}
